import { useSyncExternalStore, useCallback } from "react";

const STORAGE_KEY = "nordex-privacy-mode";

// Simple event emitter for cross-component sync
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_KEY) === "true";
}

function getServerSnapshot(): boolean {
  return false;
}

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

/**
 * Hook for privacy mode - hides sensitive balance information
 * Industry standard feature used by Binance, Bybit, OKX, etc.
 * 
 * Returns:
 * - privacyMode: boolean - whether privacy mode is enabled
 * - togglePrivacyMode: () => void - toggle function
 * - maskValue: (value: string) => string - helper to mask values
 */
export function usePrivacyMode() {
  const privacyMode = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot
  );

  const togglePrivacyMode = useCallback(() => {
    const newValue = !getSnapshot();
    localStorage.setItem(STORAGE_KEY, String(newValue));
    notifyListeners();
  }, []);

  const maskValue = useCallback(
    (value: string) => (privacyMode ? "****" : value),
    [privacyMode]
  );

  return { privacyMode, togglePrivacyMode, maskValue };
}

